import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { getSocietyAccount } from "../../services/orgService";
import { errorToast } from "../Toasts/Toasts";
import { BandBadge, BANDS } from "./SocietyBook";

// One society, everything we know about it on a page.
//
// It answers four questions in the order someone standing at the gate would
// ask them: are they a customer, who do we talk to, what happened last time,
// and what did we say we would do next. The engagement band is shown with
// the reasons that produced it, because a band nobody can interrogate is a
// number people learn to ignore.
//
// Money comes from Zoho Books, visits from the CRM. Neither is edited here.

const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);

const day = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const TEMP = { warm: "border-success", cold: "border-secondary", neutral: "border-light" };

function Stat({ label, value, note, tone = "dark" }) {
  return (
    <div className="col-6 col-md-3">
      <div className="card h-100">
        <div className="card-body py-2">
          <div className={`fs-5 fw-bold text-${tone}`}>{value}</div>
          <div className="small fw-semibold text-dark">{label}</div>
          {note && <div className="small text-muted">{note}</div>}
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  if (!value) return null;
  return (
    <div className="d-flex justify-content-between border-bottom py-1">
      <span className="small text-muted">{label}</span>
      <span className="small text-end">{value}</span>
    </div>
  );
}

const SocietyAccount = () => {
  const { accountId } = useParams();
  const [a, setA] = useState(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getSocietyAccount(accountId);
      setA(r.data || r);
    } catch (e) {
      if (e?.response?.status === 404) setMissing(true);
      else errorToast("Could not load this society");
    }
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  if (missing) {
    return (
      <div className="page-body ms-3 mt-3">
        <p className="text-muted">Not in the account book. <Link to="/societies">Go back</Link> and press Refresh.</p>
      </div>
    );
  }
  if (!a) return null;

  // The people we have actually spoken to, newest first, each listed once.
  // Matched on the number rather than the name: the same manager is written
  // down as Brajbhushan, Brij Bhushan and Brajbhushan across three visits,
  // and a name match lists one person three times.
  const contacts = [];
  for (const v of a.visits) {
    if (!v.managerName) continue;
    const digits = String(v.managerPhone || "").replace(/\D/g, "").slice(-10);
    const already = contacts.find((c) => (digits && c.digits === digits) || (!digits && c.name.toLowerCase() === v.managerName.toLowerCase()));
    if (already) {
      // keep the spelling from the most recent visit, note the rest
      if (!already.aliases.includes(v.managerName)) already.aliases.push(v.managerName);
      continue;
    }
    contacts.push({ name: v.managerName, digits, phone: v.managerPhone, designation: v.designation, lastSeen: v.visitAt, aliases: [v.managerName] });
  }
  const quotes = a.visits.filter((v) => v.quoteNumber);

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h6 className="fw-bold mb-0">
              {a.name} <BandBadge band={a.engagementBand} />
              {a.seriousRisk && <span className="badge bg-danger ms-1">risk flagged</span>}
            </h6>
            <div className="small text-muted">
              {[a.city, a.distanceKm ? `${a.distanceKm} km from the nursery` : null, a.coverageStatus].filter(Boolean).join(" · ")}
            </div>
          </div>
          <div className="small text-muted text-end">
            <Link to="/societies">← all societies</Link>
            <div>from the CRM {day(a.syncedAt)}</div>
          </div>
        </div>

        <div className="row g-2 mt-1">
          <Stat label="Billed to date" value={inr(a.billedTotal)} note={a.invoiceCount ? `${a.invoiceCount} invoice${a.invoiceCount === 1 ? "" : "s"}, last ${day(a.lastInvoiceAt)}` : "never invoiced"} />
          <Stat label="Still owed" value={inr(a.amountDue)} tone={Number(a.amountDue) > 0 ? "danger" : "dark"} note={Number(a.amountDue) > 0 ? "outstanding in Books" : "nothing outstanding"} />
          <Stat label="Visits" value={a.visitCount} note={a.daysSinceVisit === null ? "never been" : `last ${a.daysSinceVisit} days ago`} />
          <Stat label="Requirements" value={a.requirementCount} note={a.requirementCount ? `last ${day(a.lastRequirementAt)}` : "none raised here"} />
        </div>

        <div className="row g-3 mt-1">
          <div className="col-lg-5">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="fw-bold mb-1">Why {BANDS[a.engagementBand]?.label.toLowerCase() || "this band"}</h6>
                <div className="small text-muted mb-2">{BANDS[a.engagementBand]?.hint}</div>
                <ul className="mb-3 ps-3 small">
                  {a.engagementReasons.map((r) => <li key={r}>{r}</li>)}
                </ul>

                <h6 className="fw-bold mb-1">What the CRM says</h6>
                <Fact label="Category" value={a.category} />
                <Fact label="Coverage" value={a.coverageStatus} />
                <Fact label="Relationship" value={a.relationshipStatus} />
                <Fact label="Who we reach" value={a.decisionMakerStatus} />
                <Fact label="Existing vendor" value={a.existingVendorStatus} />
                <Fact label="Risk" value={a.riskCategory ? `${a.riskCategory}${a.riskScore ? ` (${a.riskScore})` : ""}` : null} />
                <Fact label="Bills as" value={a.booksContactName} />
                <Fact label="GSTIN" value={a.gstin} />
                <Fact label="Billing entity" value={a.billingEntityType} />
                <Fact label="On the portal" value={a.organization ? a.organization.name : null} />

                {a.lastMeaningfulOutcome && (
                  <div className="alert alert-light border mt-3 mb-0 py-2 px-2 small">
                    <div className="fw-semibold">Last meaningful outcome · {day(a.lastMeaningfulVisitAt)}</div>
                    {a.lastMeaningfulOutcome}
                  </div>
                )}

                {!a.organization && (
                  <div className="small text-muted mt-3">
                    Not on the portal yet. <Link to="/organizations">Onboard it</Link> to let their manager raise requirements themselves.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-7">
            {contacts.length > 0 && (
              <div className="card mb-3">
                <div className="card-body py-2">
                  <h6 className="fw-bold mb-2">People we have met</h6>
                  {contacts.slice(0, 6).map((c) => (
                    <div key={c.digits || c.name} className="d-flex justify-content-between border-bottom py-1">
                      <div className="small">
                        {c.name}
                        {c.designation && <span className="text-muted"> · {c.designation}</span>}
                        {c.aliases.length > 1 && (
                          <span className="text-muted"> · also written as {c.aliases.slice(1).join(", ")}</span>
                        )}
                      </div>
                      <div className="small text-muted">
                        {c.phone && <span className="me-2">{c.phone}</span>}
                        {day(c.lastSeen)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {quotes.length > 0 && (
              <div className="card mb-3">
                <div className="card-body py-2">
                  <h6 className="fw-bold mb-2">Quotations recorded on a visit</h6>
                  {quotes.map((v) => (
                    <div key={v.zohoVisitId} className="d-flex justify-content-between border-bottom py-1 small">
                      <span>{v.quoteNumber} · {day(v.quoteDate || v.visitAt)}</span>
                      <span>{inr(v.quoteAmount)} {v.quoteStatus && <span className="badge bg-light text-dark ms-1">{v.quoteStatus}</span>}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-body py-2">
                <h6 className="fw-bold mb-2">Every visit ({a.visits.length})</h6>
                <div style={{ maxHeight: "46vh", overflowY: "auto" }}>
                  {a.visits.map((v) => (
                    <div key={v.zohoVisitId} className={`border-start ps-2 py-2 mb-2 ${TEMP[v.temperature] || "border-light"}`} style={{ borderLeftWidth: "3px" }}>
                      <div className="d-flex justify-content-between">
                        <span className="small fw-semibold">{v.outcome || "Visit"}</span>
                        <span className="small text-muted">{day(v.visitAt)}</span>
                      </div>
                      <div className="small text-muted">
                        {[v.managerName, v.designation, v.salesperson ? `by ${v.salesperson}` : null].filter(Boolean).join(" · ")}
                      </div>
                      {v.remarks && <div className="small mt-1">{v.remarks}</div>}
                      {v.nextFollowUpAt && <div className="small text-primary mt-1">Follow up {day(v.nextFollowUpAt)}</div>}
                    </div>
                  ))}
                  {!a.visits.length && <div className="small text-muted">Nobody has been here yet.</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocietyAccount;
